import React, { useCallback, useEffect, useState } from 'react';
import { useHistory } from 'react-router';
import { useTranslation } from 'react-i18next';
import { useThunkDispatch } from '@hooks/redux';
import { useSearchParams } from '@common/hooks/use-search-params';
import { combineClassNames } from '@utils/combineClassNames';
import { EPermissions } from '@common/definitions';
import type { FailedRequest } from '@common/services/api';
import { lockDocumentTheme, unlockDocumentTheme, useTheme } from '@common/theme';
import type { StoreState } from '@common/types/store';
import {
  createTrialWithResearch, resendTrialVerifyEmail, setTrialPassword, verifyTrialEmail,
} from '../../actions';
import { useTrialResearch } from '../../hooks/use-trial-research';
import { fetchNetworks, NetworksSelectError, selectOrganisation } from '../../../organisation/actions';
import selectNetwork from '../../../network/actions/select-network';
import { colorsFromPreview, resolveTrialLogoSources, trialBrandingPayload } from '../../utils/trial-preview';
import { normaliseModulesOfInterest } from '../../utils/trial-modules';
import { industryForCreateTrialPayload, industryLabelFromPreview } from '../../utils/trial-industry';
import { TRIAL_MODULE_KEYS, type TrialGenerationStatus, type TrialResearchPreview } from '../../types';
import { DomainStep } from './domain-step';
import { ScanStep } from './scan-step';
import { PreviewStep, toEditableLocations, type PreviewValues } from './preview-step';
import { BrandStep } from './brand-step';
import { ModulesStep } from './modules-step';
import { AccountModal, type VerifyErrorKind } from './account-modal';
import { LaunchOverlay } from './launch-overlay';
import { NightMap } from './night-map';
import { DotField } from './dot-field';
import { SetupBackdrop } from './setup-backdrop';
import { BuildBackdrop } from './build-backdrop';
import { SetupChrome } from './setup-chrome';
import { TRIAL_WORDMARK, trialBackdropImageUrls } from './trial-media';
import type { DetailsErrorKind } from './details-step';
import {
  resolveDefaultAppLanguage,
  resolveTrialLanguageLocale,
  resolveTrialUiLocale,
} from './trial-locale';
import * as i18n from '../../../../i18n';

type Step = 'domain' | 'scan' | 'company' | 'brand' | 'modules' | 'account' | 'building';

const SETUP_STEP: Partial<Record<Step, number>> = {
  company: 1, brand: 2, modules: 3,
};

const initialValues = (
  preview: TrialResearchPreview,
  marketingLocale: 'nl' | 'en',
  translate: (key: string) => string,
  domain: string,
): PreviewValues => ({
  name: preview.name.trim() || domain,
  industry: industryLabelFromPreview(preview, translate),
  languageLocale: resolveDefaultAppLanguage(
    marketingLocale,
    preview.suggested_language_locale,
  ),
  locations: toEditableLocations(preview),
  modules: [...TRIAL_MODULE_KEYS],
  colors: colorsFromPreview(preview),
  logoUrl: null,
  logoFile: null,
});

const detailsErrorOf = (error: FailedRequest): DetailsErrorKind => {
  if (error.status_code === 403) return 'existing';
  if (error.status_code === 429) return 'rate_limited';
  if (error.status_code === 422 && /research_token/.test(error.detail || '')) return 'token';

  return 'failed';
};

const verifyErrorOf = (error: FailedRequest): VerifyErrorKind => {
  if (error.status_code === 422) return 'invalid';
  if (error.status_code === 429) return 'rate_limited';

  return 'failed';
};

type EnterNetwork = {
  id: string;
  is_member?: boolean;
  membership?: unknown;
};

function canViewAllNetworksOf(state: StoreState) {
  const permissions = state.permissions.organisation ?? [];

  return permissions.includes(EPermissions.ORGANISATION_NETWORKS_VIEW_ALL);
}

function pickEnterNetwork(networks: EnterNetwork[], canViewAllNetworks: boolean) {
  const eligible = networks.filter((item) => canViewAllNetworks || item.membership);

  return eligible.find((item) => item.is_member) || eligible[0] || null;
}

async function resolveEnterPath(
  dispatch: ReturnType<typeof useThunkDispatch>,
  organisationId: string,
  target: 'admin' | 'user',
) {
  const path = await dispatch(selectOrganisation(organisationId));

  if (typeof path === 'string' && path.startsWith('/auth/building')) return path;
  if (target === 'admin') return '/admin';

  const { data } = await dispatch(fetchNetworks(organisationId)) as {
    data?: EnterNetwork[];
  };
  const canViewAllNetworks = await dispatch(
    (_unused: unknown, getState: () => StoreState) => canViewAllNetworksOf(getState()),
  );
  const network = pickEnterNetwork(data ?? [], canViewAllNetworks);

  if (!network) throw new NetworksSelectError();

  const userPath = await dispatch(selectNetwork(network.id));

  if (typeof userPath !== 'string') throw new NetworksSelectError();

  return userPath;
}

const passwordErrorOf = (error: FailedRequest): DetailsErrorKind => {
  if (error.status_code === 429) return 'rate_limited';
  if (error.status_code === 422) {
    if (/password/i.test(`${error.detail || ''} ${error.error_code || ''}`)) {
      return 'invalid_password';
    }

    return 'unverified';
  }

  return 'failed';
};

const useTrialFonts = () => {
  useEffect(() => {
    if (document.getElementById('trial-fonts')) return;

    const link = document.createElement('link');

    link.id = 'trial-fonts';
    link.rel = 'stylesheet';
    link.href = 'https://fonts.googleapis.com/css2?family=Barlow+Condensed:wght@600;700'
      + '&family=Inter:wght@400;500;600;700&display=swap';
    document.head.appendChild(link);
  }, []);
};

const useTrialLightFields = () => {
  const { resolvedTheme } = useTheme();

  useEffect(() => {
    document.body.classList.add('FreeTrial--light');
    lockDocumentTheme('light');

    return () => {
      document.body.classList.remove('FreeTrial--light');
      unlockDocumentTheme(resolvedTheme);
    };
  }, [resolvedTheme]);
};

export const FreeTrialContainer = () => {
  useTrialFonts();
  useTrialLightFields();
  const history = useHistory();
  const dispatch = useThunkDispatch();
  const { searchParams } = useSearchParams<{ email?: string; domain?: string; lang?: string }>();
  const marketingLocale = resolveTrialLanguageLocale(searchParams);

  const { t, i18n: i18nInstance } = useTranslation();
  const research = useTrialResearch({ kind: 'anonymous', languageLocale: marketingLocale });
  const [step, setStep] = useState<Step>('domain');
  const [domain, setDomain] = useState(searchParams.domain || '');
  const [values, setValues] = useState<PreviewValues | null>(null);
  const [detailsError, setDetailsError] = useState<DetailsErrorKind | null>(null);
  const [verifyError, setVerifyError] = useState<VerifyErrorKind | null>(null);
  const [organisationId, setOrganisationId] = useState<string | null>(null);
  const [generation, setGeneration] = useState<TrialGenerationStatus | null>(null);
  const [accountEmail, setAccountEmail] = useState(searchParams.email || '');
  const [isEntering, setEntering] = useState(false);
  const [enterError, setEnterError] = useState<'networks' | 'failed' | null>(null);
  const [isStarting, setStarting] = useState(false);
  const [isVerifying, setVerifying] = useState(false);
  const [isResending, setResending] = useState(false);
  const [isSubmitting, setSubmitting] = useState(false);

  const { preview } = research;

  const uiLocale = resolveTrialUiLocale(marketingLocale, {
    domain: domain || searchParams.domain || null,
    suggested: preview?.suggested_language_locale,
  });

  useEffect(() => {
    if (i18nInstance.language !== uiLocale) {
      void i18n.changeLanguage(uiLocale);
    }
  }, [uiLocale, i18nInstance.language]);

  useEffect(() => {
    if (step !== 'scan') return;
    if (research.error) setStep('domain');
    if (research.isDone && preview && !values) {
      setValues(initialValues(preview, marketingLocale, t, domain));
    }
  }, [step, research.isDone, research.error, preview, marketingLocale, values, t, domain]);

  const startResearch = async (nextDomain: string) => {
    setDomain(nextDomain);
    setStep('scan');

    const started = await research.start(nextDomain);

    if (!started) setStep('domain');
  };

  const restart = () => {
    research.reset();
    setValues(null);
    setDetailsError(null);
    setVerifyError(null);
    setOrganisationId(null);
    setGeneration(null);
    setAccountEmail(searchParams.email || '');
    setStep('domain');
  };

  const startAccount = useCallback(async ({
    firstName, lastName, email,
  }: { firstName: string; lastName: string; email: string }) => {
    if (!values || !research.token || !preview) {
      setDetailsError('failed');
      return false;
    }

    setStarting(true);
    setDetailsError(null);

    try {
      const branding = await trialBrandingPayload(values, colorsFromPreview(preview), preview);
      const response = await dispatch(createTrialWithResearch({
        email,
        first_name: firstName,
        last_name: lastName,
        research_token: research.token,
        name: values.name.trim(),
        industry: industryForCreateTrialPayload(values.industry),
        language_locale: values.languageLocale,
        locations: values.locations
          .filter((location) => location.selected)
          .map((location) => ({
            place_id: location.place_id,
            name: location.name.trim(),
            is_head_office: location.is_head_office,
          })),
        modules_of_interest: normaliseModulesOfInterest(values.modules),
        ...branding,
      }));

      setAccountEmail(email);
      setOrganisationId(response.organisation_id);
      setGeneration(response.generation);

      return true;
    } catch (err) {
      setDetailsError(detailsErrorOf(err as FailedRequest));
      return false;
    } finally {
      setStarting(false);
    }
  }, [dispatch, preview, research.token, values]);

  const verifyAccount = useCallback(async (code: string) => {
    setVerifying(true);
    setVerifyError(null);

    try {
      await verifyTrialEmail(code);

      return true;
    } catch (err) {
      setVerifyError(verifyErrorOf(err as FailedRequest));
      return false;
    } finally {
      setVerifying(false);
    }
  }, []);

  const resendCode = useCallback(async () => {
    setResending(true);
    setVerifyError(null);

    try {
      await resendTrialVerifyEmail();

      return true;
    } catch (err) {
      setVerifyError(verifyErrorOf(err as FailedRequest));
      return false;
    } finally {
      setResending(false);
    }
  }, []);

  const completeAccount = useCallback(async (password: string) => {
    setSubmitting(true);
    setDetailsError(null);

    try {
      const response = await dispatch(setTrialPassword(password));

      setOrganisationId(response.organisation_id);
      setGeneration(response.generation);
      setSubmitting(false);
      setStep('building');

      return true;
    } catch (err) {
      setDetailsError(passwordErrorOf(err as FailedRequest));
      setSubmitting(false);

      return false;
    }
  }, [dispatch]);

  const enterApp = useCallback(async (target: 'admin' | 'user') => {
    if (!organisationId || isEntering) return;

    setEntering(true);
    setEnterError(null);

    try {
      history.replace(await resolveEnterPath(dispatch, organisationId, target));
    } catch (err) {
      setEntering(false);
      setEnterError(err instanceof NetworksSelectError ? 'networks' : 'failed');
    }
  }, [dispatch, history, isEntering, organisationId]);

  const setupIndex = SETUP_STEP[step];
  const showBuild = step === 'account' || step === 'building';
  const trialLogo = preview && values ? resolveTrialLogoSources(preview, values) : null;

  return (
    <div id="Login" className={combineClassNames('FreeTrial', `FreeTrial--step-${step}`)}>
      {step === 'domain' && <NightMap />}
      {step === 'scan' && <DotField />}
      {setupIndex && values && (
        <SetupBackdrop
          company={values.name}
          brand={values.colors.main}
          accent={values.colors.button}
          locations={values.locations.filter((location) => location.selected).map((location) => location.name)}
          images={trialBackdropImageUrls(preview)}
          wide={step === 'modules'}
        />
      )}
      {showBuild && values && (
        <BuildBackdrop
          company={values.name}
          brand={values.colors.main}
          iconUrl={trialLogo?.iconUrl}
          logoUrl={trialLogo?.logoUrl}
          languageLocale={values.languageLocale}
          email={accountEmail}
          organisationId={organisationId}
          initialGeneration={generation}
          selectedModules={values.modules}
          interactive={step === 'building'}
          isEntering={isEntering}
          enterError={enterError}
          onEnterAdmin={() => enterApp('admin')}
          onEnterUser={() => enterApp('user')}
        />
      )}

      {step === 'domain' && (
        <>
          <img className="FreeTrial__Mark max-md:!tw-left-5 max-md:!tw-top-[22px] max-md:!tw-h-6" src={TRIAL_WORDMARK} alt="" />
          <DomainStep
            initialValue={domain}
            error={research.error}
            isSubmitting={research.isStarting}
            onSubmit={startResearch}
          />
        </>
      )}

      {step === 'scan' && (
        <>
          <img className="FreeTrial__Mark max-md:tw-hidden" src={TRIAL_WORDMARK} alt="" />
          <ScanStep
            domain={domain}
            research={research.research}
            preview={preview}
            isDone={!!research.isDone && !!values}
            onContinue={() => setStep('company')}
          />
        </>
      )}

      {setupIndex && (
        <>
          <SetupChrome current={setupIndex} domain={domain} />
          <div className="FreeTrial__Setup max-md:!tw-min-h-[100dvh] max-md:!tw-items-end max-md:!tw-p-0">
            {step === 'company' && values && (
              <PreviewStep
                values={values}
                onChange={setValues}
                onContinue={() => setStep('brand')}
                onReject={restart}
              />
            )}
            {step === 'brand' && preview && values && (
              <BrandStep
                preview={preview}
                values={values}
                onChange={setValues}
                onContinue={() => setStep('modules')}
                onBack={() => setStep('company')}
              />
            )}
            {step === 'modules' && values && (
              <ModulesStep
                values={values}
                onChange={setValues}
                onContinue={() => setStep('account')}
                onBack={() => setStep('brand')}
              />
            )}
          </div>
        </>
      )}

      {step === 'account' && values && (
        <>
          <LaunchOverlay
            company={values.name}
            brand={values.colors.main}
            iconUrl={trialLogo?.iconUrl}
            logoUrl={trialLogo?.logoUrl}
          />
          <AccountModal
            initialEmail={searchParams.email || ''}
            error={detailsError}
            verifyError={verifyError}
            isStarting={isStarting}
            isVerifying={isVerifying}
            isResending={isResending}
            isSubmitting={isSubmitting}
            onStart={startAccount}
            onVerify={verifyAccount}
            onResend={resendCode}
            onComplete={completeAccount}
          />
        </>
      )}
    </div>
  );
};

export default FreeTrialContainer;
